import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Formik, Form, Field } from 'formik'
import { useModal } from 'react-modal-hook'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LayoutWithAccountCard from '../../../../layout/Layout/layout-account-card'
import {
  BENEFICIARY_TRANSFER_KEYS,
  DIALOG_KEYS,
  SIDENAV_KEYS,
  PROFILE_KEYS,
  ADD_BENEFICIARY,
} from '../../../../utils/translation-constants'
import { NeoLoader, NeoOTP, NeoSuccess } from '../../../../components/FormComponents'
import NeoButton from '../../../../components/NeoButton/NeoButton'
import NeoInput from '../../../../components/NeoInput/NeoInput'
import InputError from '../../../../components/InputError/InputError'
import Loading from 'custom/assets/loaders/Loading'
import Modal from '../../../../components/Modal/Modal'
import BeneficiaryTransferService from '../../../../services/beneficiary-transfers.service'
import BeneIcon from 'custom/assets/common-icons/bene-image.svg'

import DeleteIcon from 'custom/assets/icons/delete.svg'
import UploadIcon from 'custom/assets/icons/upload.svg'
import { EditOtherBeneficiarySchema } from './../../../../schemas/beneficiary-transfer/index'
import NumberFormat from 'react-number-format'
import Config from 'custom/config/environment'

import validationConfig from 'custom/config/validation.config.json'
const regionalBeneConfig = validationConfig['addBeneficiary']['regional']

export default function EditRegionalBeneficiary() {
  const history = useHistory()
  const location = useLocation()
  const [stage, setStage] = useState(0)
  const [otpEnabled, setOtpEnabled] = useState(false)
  const [formValues, setFormValues] = useState({})
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [otp, setOtp] = useState('')
  const { t } = useTranslation()
  const [verifiedBeneDetails, setVerifiedBeneDetails] = useState({})
  const [beneDetails, setBeneDetails] = useState({})
  const [imageLocation, setImageLocation] = useState('')
  const [deleteImageLoading, setDeleteImageLoading] = useState(false)
  const [updateImageLoading, setUpdateImageLoading] = useState(false)
  const beneImage = useRef(null)

  const [showSuccessModal, hideSuccessModal] = useModal(
    () => (
      <Modal>
        <div className='modal-error-body'>
          <h6>{message}</h6>
          <div className='modal-btn-style'>
            <NeoButton
              mode='newDesign'
              type='button'
              text={t(DIALOG_KEYS.CLOSE)}
              onClick={() => {
                hideSuccessModal()
              }}
            />
          </div>
        </div>
      </Modal>
    ),
    [message],
  )

  useEffect(() => {
    if (location.state === undefined) {
      history.push('/beneficiary-transfers/view')
      return
    }
    setBeneDetails(location?.state?.beneDetails)
  }, [])

  const handleFileRead = useCallback(
    async (e) => {
      const content = e.target.result
      await updateImage(content.toString().split(',')[1], beneDetails)
    },
    [beneDetails],
  )

  useEffect(() => {
    const fileReader = new FileReader()
    fileReader.onloadend = handleFileRead
    if (imageLocation !== '') {
      fileReader.readAsDataURL(imageLocation)
    }
  }, [imageLocation, handleFileRead])

  const deleteImage = useCallback(async () => {
    setDeleteImageLoading(true)
    try {
      const response = await BeneficiaryTransferService.deleteBeneficiaryImage({
        accountNo: beneDetails.beneAccNo,
      })
      if (response.status === 200) {
        setMessage(response.data.message)
        setStage(4)
        setDeleteImageLoading(false)
        // beneDetails.image = null
      }
    } catch (err) {
      setDeleteImageLoading(false)
    }
  }, [beneDetails])

  const updateImage = useCallback(async (imageData, beneDetails) => {
    setUpdateImageLoading(true)
    try {
      const response = await BeneficiaryTransferService.changeBeneficiaryImage({
        accountNo: beneDetails.beneAccNo,
        imageData: imageData,
      })
      if (response.status === 200) {
        beneDetails.image = imageData
        setMessage(response.data.message)
        setStage(4)
        setUpdateImageLoading(false)
      }
    } catch (err) {
      setUpdateImageLoading(false)
      showSuccessModal()
    }
  }, [])

  const onVerify = useCallback(async (data) => {
    try {
      if (data.transferAmount < Config.MIN_TRANSFER_LIMIT) {
        return
      }
      setFormValues(data)
      setLoading(true)
      const response = await BeneficiaryTransferService.verifyEditOtherBeneficiary({
        shortName: data?.nickName,
        srcAccount: data?.accountNumber,
        active: 'Y',
        maxTransferLimit: data?.maxTransferAmount?.replace(/\,/g, ''),
        beneEMail: data?.beneEMail,
        beneMobileNo: data?.beneMobileNo,
        beneBrn: beneDetails?.beneBrn,
      })
      if (response.status === 200) {
        setOtpEnabled(response.data['otpEnable'])
        setStage(2)
        setLoading(false)
      }
    } catch (err) {
      setLoading(false)
    }
  }, [])
  const onConfirm = useCallback(async () => {
    try {
      setLoading(true)
      const response = await BeneficiaryTransferService.editOtherBeneficiary({
        beneId: beneDetails?.beneId,
        shortName: formValues?.nickName,
        srcAccount: formValues?.accountNumber,
        active: 'Y',
        maxTransferLimit: formValues?.maxTransferAmount?.replace(/\,/g, ''),
        otp: otp || '0000',
        beneEMail: formValues?.beneEMail,
        beneMobileNo: formValues?.beneMobileNo,
        beneBrn: beneDetails?.beneBrn,
      })
      if (response.status === 200) {
        setMessage(response.data['message'])
        setLoading(false)
        setStage(4)
      }
    } catch (err) {
      setLoading(false)
      setOtp('')
    }
  }, [formValues, otp, beneDetails])

  const verifyRemoveBeneficiary = useCallback(
    async (data) => {
      try {
        setLoading(true)
        const response = await BeneficiaryTransferService.removeBeneficiaryVerify({
          beneId: beneDetails?.beneId,
          internalBene: false,
          srcAccount: beneDetails?.beneAccNo,
        })
        if (response.status === 200) {
          if (response.data['otpEnable']) {
            setOtpEnabled(response.data['otpEnable'])
            setStage(5)
            setLoading(false)
          } else {
            await onRemoveConfirm()
          }
        }
      } catch (err) {
        setLoading(false)
      }
    },
    [beneDetails],
  )

  const onRemoveConfirm = useCallback(async () => {
    try {
      setLoading(true)
      const response = await BeneficiaryTransferService.removeBeneficiary({
        beneId: beneDetails?.beneId,
        internalBene: false,
        srcAccount: beneDetails?.beneAccNo,
        otp: otp || '0000',
      })
      if (response.status === 200) {
        setMessage(response.data['message'])
        setLoading(false)
        setStage(4)
      }
    } catch (err) {
      setLoading(false)
      setOtp('')
    }
  }, [beneDetails, otp])

  function renderSwitch() {
    switch (stage) {
      case 0:
        return renderBeneDetails()
      case 1:
        return renderForm()
      case 2:
        return renderConfirmation()
      case 3:
        return <NeoOTP otp={otp} setOTP={setOtp} onSubmit={onConfirm} loading={loading} />

      case 4:
        return <NeoSuccess message={message} linkPath='/beneficiary-transfers/view' />
      case 5:
        return <NeoOTP otp={otp} setOTP={setOtp} onSubmit={onRemoveConfirm} loading={loading} />
      default:
        return renderForm()
    }
  }

  function renderForm() {
    return (
      <div>
        <Formik
          initialValues={{
            nickName: beneDetails?.shortName,
            accountNumber: beneDetails?.beneAccNo,
            transferAmount: beneDetails?.maxTransferLimit,
            maxTransferAmount: '' + beneDetails?.maxTransferLimit,
            beneEMail: beneDetails?.beneEMail,
            beneMobileNo: beneDetails?.beneMobileNo,
          }}
          enableReinitialize
          validationSchema={EditOtherBeneficiarySchema}
          onSubmit={onVerify}>
          {({ isSubmitting, errors, touched, handleChange, values, setFieldValue }) => (
            <Form>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    showAsterisk={regionalBeneConfig['isNickNameRequired']}
                    text={t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.NICK_NAME)}
                    name='nickName'
                    value={values.nickName}
                    onChange={handleChange}
                  />
                  <InputError
                    text={errors.nickName}
                    onChange={handleChange}
                    touched={touched.nickName}
                  />
                </div>

                <div className='form-group col-12'>
                  <NeoInput
                    showAsterisk={regionalBeneConfig['isEmailRequired']}
                    text={t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.BENE_EMAIL)}
                    name='beneEMail'
                    value={values.beneEMail}
                    onChange={handleChange}
                  />
                  <InputError text={errors.beneEMail} touched={touched.beneEMail} />
                </div>

                <div className='form-group col-12'>
                  <NeoInput
                    showAsterisk={regionalBeneConfig['isMobileNoRequired']}
                    text={t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.BENE_MOBILE)}
                    onChange={(value) => {
                      setFieldValue('beneMobileNo', value?.value)
                    }}
                    mode='onlyNumber'
                    name='beneMobileNo'
                    minLength={8}
                    maxLength={15}
                  />
                  <InputError text={errors.beneMobileNo} touched={touched.beneMobileNo} />
                </div>

                <div className='form-group col-12'>
                  <NeoInput
                    showAsterisk={regionalBeneConfig['isAccountNoRequired']}
                    text={t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.TRANSFER_LIMIT)}
                    type='number'
                    mode='amount'
                    value={values.maxTransferAmount}
                    onChange={(value) => {
                      setFieldValue('transferAmount', value.floatValue)
                      setFieldValue('maxTransferAmount', value?.value)
                    }}
                    name='maxTransferAmount'
                  />
                  <InputError text={errors.maxTransferAmount} touched={touched.maxTransferAmount} />

                  <InputError
                    text={`${t(BENEFICIARY_TRANSFER_KEYS.TRANSFER_LIMIT_TXT)} ${
                      Config.MIN_TRANSFER_LIMIT
                    }.00`}
                    touched={values.transferAmount < Config.MIN_TRANSFER_LIMIT}
                  />
                </div>
              </div>

              <div className='mt-3'>
                {loading ? (
                  renderLoading()
                ) : (
                  <NeoButton
                    type='submit'
                    text={t(ADD_BENEFICIARY.SUBMIT)}
                    disabled={isSubmitting}
                    mode='newDesign'
                  />
                )}
              </div>
              <div className='back-cancel-style' onClick={() => setStage(0)}>
                {' '}
                {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
              </div>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  function renderBeneDetails() {
    return (
      <>
        <div className='row'>
          <div className='col-6'>
            <NeoButton
              type='button'
              mode='newDesign'
              className='btn btn-neo'
              onClick={() => setStage(1)}
              text={t(PROFILE_KEYS.MODIFY_BEN)}
            />
          </div>
          <div className='col-6'>
            {loading ? (
              renderLoading()
            ) : (
              <NeoButton
                type='button'
                mode='newDesign'
                className='btn btn-neo'
                onClick={() => verifyRemoveBeneficiary()}
                text={t(PROFILE_KEYS.DELETE_BEN)}
              />
            )}
          </div>
        </div>

        <div className='back-cancel-style' onClick={() => history.goBack()}>
          {t(SIDENAV_KEYS.TOUCH_ID_KEYS.CANCEL)}
        </div>
      </>
    )
  }

  function renderConfirmation() {
    return (
      <div className='d-flex flex-column confirmation'>
        <div>
          <h6 className='label'>
            {t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.NICK_NAME)}
          </h6>
          <h6 className='value'>{formValues.nickName}</h6>
        </div>
        <div>
          <h6 className='label'>
            {t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.BENE_EMAIL)}
          </h6>
          <h6 className='value'>{formValues.beneEMail}</h6>
        </div>
        <div>
          <h6 className='label'>
            {t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.BENE_MOBILE)}
          </h6>
          <h6 className='value'>{formValues.beneMobileNo}</h6>
        </div>
        <div>
          <h6 className='label'>
            {t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.TRANSFER_LIMIT)}
          </h6>
          <h6 className='value'>
            <NumberFormat
              value={formValues?.maxTransferAmount}
              displayType={'text'}
              thousandSeparator={true}
              prefix={''}
              fixedDecimalScale={true}
              decimalScale={2}
              allowEmptyFormatting
            />
          </h6>
        </div>

        <div className='mt-3'>
          {loading ? (
            renderLoading()
          ) : (
            <NeoButton
              type='button'
              mode='newDesign'
              className='btn btn-neo'
              onClick={() => (otpEnabled ? setStage(3) : onConfirm())}
              text={t(ADD_BENEFICIARY.CONFIRM)}
            />
          )}
        </div>
        <div className='back-cancel-style' onClick={() => setStage(1)}>
          {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
        </div>
      </div>
    )
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  return (
    <LayoutWithAccountCard breadCumKey='BENE_REGIONAL_MODIFY'>
      <div className='new-design-form-style'>
        <div className='heading-style'>
          {t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.MODIFYREGIONAL)}
        </div>
        {stage !== 3 && stage !== 4 && stage !== 5 && (
          <>
            <div className='bene-modify-details'>
              <img
                className='bene-image'
                src={beneDetails?.image === null ? BeneIcon : beneDetails?.image}
                width={160}
                height={160}
              />
              {stage == 0 && (
                <>
                  {deleteImageLoading ? (
                    <NeoLoader />
                  ) : (
                    <>
                      {beneDetails?.image !== null && (
                        <img
                          onClick={() => deleteImage()}
                          src={DeleteIcon}
                          className='delete-image'
                        />
                      )}
                    </>
                  )}
                </>
              )}
              {stage == 0 && (
                <>
                  {updateImageLoading ? (
                    <NeoLoader />
                  ) : (
                    <button
                      style={{ margin: '0 auto' }}
                      type='button'
                      ref={beneImage}
                      onClick={() => {
                        beneImage.current.click()
                      }}
                      className=' d-flex btn btn-sm btn-link small new-design-warning-text-color font-weight-600'>
                      <img src={UploadIcon} className='mr-2' />
                      {t(PROFILE_KEYS.MODIFY)}
                    </button>
                  )}
                  <input
                    type='file'
                    id='file'
                    accept='image/png, image/jpeg, image/jpg'
                    ref={beneImage}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      var size = e.target.files[0].size / 1000
                      if (size > 100) {
                        setMessage(t(PROFILE_KEYS.UPLOAD_MESSAGE))
                        e.target.value = null
                        showSuccessModal()
                        return
                      }
                      setImageLocation(e.target.files[0])
                      e.target.value = null
                    }}
                  />
                </>
              )}
            </div>

            <div className='d-flex flex-column confirmation'>
              <div>
                <h6 className='label'>{t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_NAME)}</h6>
                <h6 className='value'>{beneDetails?.beneName}</h6>
              </div>

              {stage == 0 && (
                <div>
                  <h6 className='label'>{t(ADD_BENEFICIARY.DOMESTIC.NICK_NAME)}</h6>
                  <h6 className='value'>{beneDetails?.shortName}</h6>
                </div>
              )}
              <div>
                <h6 className='label'>{t(ADD_BENEFICIARY.DOMESTIC.ACCOUNT_NUMBER)}</h6>
                <h6 className='value'>{beneDetails?.beneAccNo}</h6>
              </div>
              {stage == 0 && (
                <div>
                  <h6 className='label'> {t(ADD_BENEFICIARY.DOMESTIC.TRANSFER_LIMIT)}</h6>
                  <h6 className='value'>
                    <NumberFormat
                      value={beneDetails?.maxTransferLimit}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={''}
                      fixedDecimalScale={true}
                      decimalScale={2}
                      allowEmptyFormatting
                    />
                  </h6>
                </div>
              )}
            </div>
          </>
        )}

        {renderSwitch()}
      </div>
    </LayoutWithAccountCard>
  )
}
